import type * as ToneNS from 'tone';
import { BPM_MAX, BPM_MIN, type AvMusicSource, type Project } from '../domain/schemas';
import { sessionTimeline } from '../av/events';
import { debugCounters } from '../lib/debug';
import { Observable } from '../lib/store';
import {
  PPQ,
  TICKS_PER_STEP,
  compileTimeline,
  requiredAudio,
  secondsPerTick,
  sectionAtStep,
  type CompiledTimeline,
  type PlaybackTarget,
} from '../music/compile';
import { AssetLoadError } from './assets/bufferCache';
import { buildGraph, scheduleTimeline, type Graph, type ToneModule } from './graph';
import type { RenderAudio } from './render';

export type { ToneModule } from './graph';

export type AudioEngineStatus = 'idle' | 'starting' | 'playing' | 'stopped' | 'error';

export interface AudioEngineSnapshot {
  status: AudioEngineStatus;
  bpm: number;
  /** State of the underlying AudioContext, or 'none' before the first user gesture. */
  contextState: 'none' | AudioContextState;
  /** Step within the playing timeline (pattern or whole song), or -1 while stopped. */
  step: number;
  mode: 'pattern' | 'song';
  totalSteps: number;
  /** Section under the playhead in song mode. */
  sectionId: string | null;
  /** Number of scheduled parts (should only ever be 0 or 1). */
  scheduledParts: number;
  error?: string;
  /** A sample or clip that could not be loaded; playback continues without it (never substituted). */
  assetError?: string;
  /** Samples/clips being decoded for playback. */
  loadingAudio: boolean;
}

export const clampBpm = (bpm: number): number => Math.round(Math.min(BPM_MAX, Math.max(BPM_MIN, bpm)));

/**
 * Phase 4A: what an audio-visual session asks the engine to play — its music source,
 * from a musical position, optionally looping a tick range (Tone's transport loop, the
 * same range the session clock folds time with).
 */
export interface SessionPlayback {
  music: AvMusicSource;
  fromTicks: number;
  loop: { startTicks: number; endTicks: number } | null;
}

/** Audio could not start (autoplay policy, suspended or unavailable context). */
export class AudioBlockedError extends Error {
  constructor(message = 'The browser blocked audio. Click Play again to allow sound, or keep previewing silently.') {
    super(message);
    this.name = 'AudioBlockedError';
  }
}

/** A newer start/stop request superseded this session start (rapid play/pause/seek). */
export class SessionStartCancelled extends Error {
  constructor() {
    super('Superseded by a newer playback request.');
    this.name = 'SessionStartCancelled';
  }
}

/** Seconds scheduled ahead of "now" for a session start, so the first notes are never late. */
export const SESSION_START_LEAD = 0.08;
/** How long `startSession` waits for a suspended context to resume before reporting it blocked. */
export const RESUME_TIMEOUT_MS = 1500;

interface Live {
  tone: ToneModule;
  context: ToneNS.Context;
  graph: Graph;
  part: ToneNS.Part | null;
  playheadId: number | null;
  endId: number | null;
  timeline: CompiledTimeline;
  /** What the scheduled Part plays: the project and session music/loop it was built for. */
  scheduled: { music: unknown[]; session: string | null } | null;
}

/** Everything the scheduled Part depends on (the same fields `setProject` reschedules for). */
const musicOf = (p: Project): unknown[] => [p.patterns, p.arrangement, p.activePatternId, p.settings.swing, p.settings.bpm, p.audioClips, p.assets, p.transport.mode, p.transport.loopSong];
const sameMusic = (a: unknown[], b: unknown[]) => a.length === b.length && a.every((x, i) => x === b[i]);

const sessionKey = (p: SessionPlayback | null) => (p ? JSON.stringify([p.music, p.loop]) : null);

/**
 * Realtime multitrack engine built on Tone.js.
 *
 * Lifecycle rules:
 *  - Nothing audio-related (not even the Tone.js module) is loaded until `start()`.
 *  - `start()` must be called from a user gesture; it creates a dedicated AudioContext.
 *  - At most one Tone.Part is ever scheduled; rescheduling disposes the previous one,
 *    so edits during playback can't double-trigger notes.
 *  - `stop()` stops the transport, releases every voice (no stuck notes), rewinds to
 *    the start and suspends the context. `dispose()` frees every node and closes it.
 */
export class AudioEngine {
  readonly state = new Observable<AudioEngineSnapshot>({
    status: 'idle',
    bpm: 96,
    contextState: 'none',
    step: -1,
    mode: 'pattern',
    totalSteps: 0,
    sectionId: null,
    scheduledParts: 0,
    loadingAudio: false,
  });
  private live: Live | null = null;
  private building: Promise<Live> | null = null;
  private project: Project | null = null;
  private disposed = false;
  private readonly loadTone: () => Promise<ToneModule>;
  private readonly audio: RenderAudio | undefined;
  /** Assets pinned in the cache while playing. */
  private pins: { key: string; release(): void } | null = null;
  private audioRequest = 0;
  /** Phase 4A: the session being played (replaces the project's own transport target). */
  private session: SessionPlayback | null = null;
  private sessionRequest = 0;
  /**
   * Context time at which the last transport stop takes effect. Tone's `stop()` without a
   * time stops at now() = currentTime + lookAhead, so a start scheduled earlier than that
   * would be cancelled by the pending stop; session starts are scheduled after it.
   */
  private lastStopAt = 0;

  constructor(loadTone: () => Promise<ToneModule> = () => import('tone'), audio?: RenderAudio) {
    this.loadTone = loadTone;
    this.audio = audio;
  }

  /** Every asset the current project can play right now (sample slots + clips of the target). */
  private wantedAudio(project: Project): { assetId: string; reversed: boolean }[] {
    const timeline = this.timelineFor(project);
    const wanted = requiredAudio(project, timeline);
    // Samples assigned to tracks without notes in this pattern are loaded too, so
    // switching patterns doesn't drop hits while they decode.
    for (const t of project.tracks) {
      if (t.sample && !wanted.some((w) => w.assetId === t.sample!.assetId && w.reversed === t.sample!.reverse)) {
        wanted.push({ assetId: t.sample.assetId, reversed: t.sample.reverse });
      }
    }
    return wanted;
  }

  private assetMessage(project: Project, error: unknown): string {
    if (error instanceof AssetLoadError) {
      if (error.code === 'budget') return error.message;
      const name = project.assets.find((a) => a.id === error.assetId)?.name ?? 'An audio file';
      return `“${name}” cannot play: ${error.message}`;
    }
    return error instanceof Error ? error.message : 'Audio files could not be loaded.';
  }

  /**
   * Loads and pins what the project needs. Throws on failure when `strict` (start),
   * otherwise reports it in `assetError` and keeps playing what is loaded.
   */
  private async loadAudio(project: Project, strict: boolean): Promise<void> {
    const wanted = this.wantedAudio(project);
    const key = wanted.map((w) => `${w.assetId}:${w.reversed}`).sort().join('|');
    if (this.pins?.key === key) return;
    if (!this.audio) {
      if (wanted.length === 0) return;
      throw new Error('Audio files cannot be played in this environment.');
    }
    const request = ++this.audioRequest;
    this.state.update({ loadingAudio: wanted.length > 0 });
    try {
      const handle = await this.audio.acquire(wanted);
      if (request !== this.audioRequest) {
        handle.release();
        return;
      }
      this.pins?.release();
      this.pins = { key, release: () => handle.release() };
      this.state.update({ loadingAudio: false, assetError: undefined });
    } catch (error) {
      if (request !== this.audioRequest) return;
      const assetError = this.assetMessage(project, error);
      this.state.update({ loadingAudio: false, assetError });
      if (strict) throw new Error(assetError);
    }
  }

  private releaseAudio(): void {
    this.audioRequest++;
    this.pins?.release();
    this.pins = null;
  }

  get snapshot(): AudioEngineSnapshot {
    return this.state.get();
  }

  private target(project: Project): PlaybackTarget {
    return project.transport.mode === 'song' ? { mode: 'song' } : { mode: 'pattern', patternId: project.activePatternId };
  }

  /** The timeline that plays: a session's music while a session owns playback, else the project's transport target. */
  private timelineFor(project: Project): CompiledTimeline {
    return this.session ? sessionTimeline(project, this.session.music) : compileTimeline(project, this.target(project));
  }

  /** True while an audio-visual session owns playback (Phase 4A). */
  get sessionActive(): boolean {
    return this.session !== null;
  }

  /**
   * Updates the engine from the project. Mixer-only changes are applied in place;
   * note, arrangement, swing or mode changes reschedule the single Part.
   */
  setProject(project: Project): void {
    const previous = this.project;
    this.project = project;
    const bpm = clampBpm(project.settings.bpm);
    const live = this.live;
    if (bpm !== this.snapshot.bpm) {
      live?.tone.getTransport().bpm.rampTo(bpm, 0.05);
      live?.graph.setTempo(bpm);
      this.state.update({ bpm });
    }
    if (this.snapshot.mode !== project.transport.mode) this.state.update({ mode: project.transport.mode });
    if (!live) return;
    if (
      !previous ||
      previous.tracks !== project.tracks ||
      previous.audioBus !== project.audioBus ||
      previous.settings.masterVolumeDb !== project.settings.masterVolumeDb
    ) {
      live.graph.applyMix(project.tracks, project.settings.masterVolumeDb, project.audioBus);
    }
    const playing = this.snapshot.status === 'playing';
    if (playing && (previous?.tracks !== project.tracks || previous?.audioClips !== project.audioClips || previous?.assets !== project.assets)) {
      void this.loadAudio(project, false);
    }
    // Clip timing depends on tempo (clip audio is never stretched), so clips recompile on BPM changes.
    const clipsChanged =
      !previous ||
      previous.audioClips !== project.audioClips ||
      previous.assets !== project.assets ||
      (project.audioClips.length > 0 && previous.settings.bpm !== project.settings.bpm);
    // While a session plays, its music source decides what plays, not the transport mode.
    const modeChanged = !this.session && previous?.transport.mode !== project.transport.mode;
    const musicChanged =
      !previous ||
      modeChanged ||
      previous.patterns !== project.patterns ||
      previous.arrangement !== project.arrangement ||
      previous.activePatternId !== project.activePatternId ||
      previous.settings.swing !== project.settings.swing ||
      previous.transport.loopSong !== project.transport.loopSong ||
      clipsChanged;
    if (musicChanged) {
      const transport = live.tone.getTransport();
      if (modeChanged && playing) {
        // Switching between pattern and song restarts from the top.
        transport.stop();
        live.graph.releaseAll();
        transport.position = 0;
      }
      this.reschedule(live);
      if (modeChanged && playing) transport.start('+0.05');
      else if (clipsChanged && playing) this.restartClips(live);
    }
  }

  /**
   * After a clip edit during playback: stop clip voices and restart every clip that
   * overlaps the playhead from the matching point, so edits are heard immediately and
   * no clip ever plays twice.
   */
  private restartClips(live: Live): void {
    live.graph.stopClips();
    const transport = live.tone.getTransport();
    const now = live.context.currentTime + 0.03;
    const ticks = transport.getTicksAtTime(now);
    const spt = secondsPerTick(clampBpm(this.project!.settings.bpm));
    for (const clip of live.timeline.clips) {
      const into = (ticks - clip.tick) * spt;
      if (into > 0.01 && into < clip.playSec) live.graph.playClip(clip, now, into);
    }
  }

  /** Oscillators and buffer sources currently scheduled or sounding (diagnostics). */
  liveVoices(): number {
    return this.live?.graph.liveVoices() ?? 0;
  }

  /** Current output level in dBFS (−Infinity when silent or not started). */
  getLevel(): number {
    return this.live?.graph.getLevel() ?? -Infinity;
  }

  /** Must be invoked from a user gesture (click / key press). */
  async start(): Promise<void> {
    if (this.disposed) throw new Error('Audio engine has been disposed.');
    if (!this.project) throw new Error('No project is loaded.');
    const status = this.snapshot.status;
    if (status === 'playing' || status === 'starting') return;
    // A stop() that is still suspending the context must not report this start as stopped,
    // and a stop() (or a session start) that arrives while this start awaits wins.
    const request = ++this.sessionRequest;
    const cancelled = () => this.disposed || request !== this.sessionRequest;
    if (this.project.transport.mode === 'song' && this.project.arrangement.sections.length === 0) {
      throw new Error('The arrangement is empty. Add a section or switch to pattern mode.');
    }
    this.state.update({ status: 'starting', error: undefined, assetError: undefined });
    try {
      const live = await this.ensureLive();
      if (cancelled()) return;
      await live.context.resume();
      if (cancelled()) return;
      // Samples and clips must be decoded before the first note: a missing file is an error, not silence.
      await this.loadAudio(this.project, true);
      if (cancelled()) return;
      // Apply anything that changed while the graph was being built.
      const project = this.project;
      live.graph.applyMix(project.tracks, project.settings.masterVolumeDb, project.audioBus);
      live.graph.setTempo(clampBpm(project.settings.bpm));
      live.tone.getTransport().bpm.value = clampBpm(project.settings.bpm);
      // A clean slate: no voice from an earlier run may play when the context resumes.
      live.graph.releaseAll();
      this.reschedule(live);
      const transport = live.tone.getTransport();
      transport.position = 0;
      // "+0.05" is relative to now() = currentTime + lookAhead: always after the last stop.
      transport.start('+0.05');
      this.state.update({ status: 'playing', contextState: live.context.state });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Audio could not start.';
      this.releaseAudio();
      // Don't leave a running context behind when nothing is playing.
      await (this.live?.context.rawContext as AudioContext | undefined)?.suspend?.().catch(() => undefined);
      this.state.update({ status: 'error', error: message, contextState: this.live?.context.state ?? this.snapshot.contextState });
      throw error;
    } finally {
      debugCounters.audioState = this.snapshot.status;
    }
  }

  async stop(): Promise<void> {
    const request = ++this.sessionRequest;
    this.session = null;
    const live = this.live;
    if (!live) {
      if (this.snapshot.status !== 'idle') this.state.update({ status: 'stopped', step: -1, sectionId: null });
      return;
    }
    // Stop the transport NOW. Tone's stop() without a time takes effect at now() =
    // currentTime + lookAhead, and until then its clock keeps dispatching Part events: notes
    // in that window would start after releaseAll() below, never be released, and play
    // when the context next resumes.
    this.stopTransportNow(live);
    live.tone.getTransport().position = 0;
    live.graph.releaseAll();
    this.releaseAudio();
    // Stopped as of now (the UI offers Start at once); suspending is only a power optimisation.
    this.state.update({ status: 'stopped', step: -1, sectionId: null });
    debugCounters.audioState = 'stopped';
    try {
      await (live.context.rawContext as AudioContext).suspend();
    } catch {
      // Suspending is a power optimisation only.
    }
    // A start issued meanwhile owns the engine now (its resume follows this suspend).
    if (request !== this.sessionRequest) return;
    this.state.update({ contextState: live.context.state });
  }

  /**
   * Phase 4A: plays an audio-visual session's music from `fromTicks` (must be called from
   * a user gesture the first time). Returns the AudioContext time at which `fromTicks`
   * will be *heard* — in the same timebase as `audibleTime()` — so the session clock can
   * anchor to it. Calling it while playing restarts the transport at the new position
   * (seek). Throws AudioBlockedError when the context cannot run (autoplay policy) and
   * SessionStartCancelled when a newer start or a stop superseded it.
   */
  async startSession(playback: SessionPlayback, resumeTimeoutMs = RESUME_TIMEOUT_MS): Promise<number> {
    if (this.disposed) throw new Error('Audio engine has been disposed.');
    if (!this.project) throw new Error('No project is loaded.');
    const request = ++this.sessionRequest;
    const current = this.live;
    if (current && this.snapshot.status === 'playing') {
      // Seeking (or taking over from the Music workspace): stop the transport and every voice.
      this.stopTransportNow(current);
      current.graph.releaseAll();
    }
    this.session = playback;
    this.state.update({ status: 'starting', error: undefined, assetError: undefined });
    const cancelled = () => this.disposed || request !== this.sessionRequest;
    try {
      const live = await this.ensureLive();
      if (cancelled()) throw new SessionStartCancelled();
      // Always resume, even when the context reports 'running': a stop() that is still
      // suspending it (pause → play in quick succession) would otherwise land after the
      // transport starts and freeze it. A resume queues after any pending suspend.
      let timer: ReturnType<typeof setTimeout> | undefined;
      await Promise.race([
        Promise.resolve(live.context.resume()).catch(() => undefined),
        new Promise<void>((resolve) => (timer = setTimeout(resolve, resumeTimeoutMs))),
      ]);
      clearTimeout(timer);
      if (cancelled()) throw new SessionStartCancelled();
      if (live.context.state !== 'running') throw new AudioBlockedError();
      await this.loadAudio(this.project, true);
      if (cancelled()) throw new SessionStartCancelled();
      const project = this.project;
      live.graph.applyMix(project.tracks, project.settings.masterVolumeDb, project.audioBus);
      live.graph.setTempo(clampBpm(project.settings.bpm));
      const transport = live.tone.getTransport();
      transport.bpm.value = clampBpm(project.settings.bpm);
      // A seek within the same session and project reuses the scheduled Part (rebuilding a
      // 3-minute song's schedule on every seek would cost hundreds of ms).
      if (!live.part || !live.scheduled || !sameMusic(live.scheduled.music, musicOf(project)) || live.scheduled.session !== sessionKey(playback)) this.reschedule(live);
      // An overlapping request may have started the transport while this one awaited: stop
      // it here, after the last await, or start() would keep the old position.
      if (transport.state === 'started') {
        this.stopTransportNow(live);
        live.graph.releaseAll();
      }
      const from = Math.max(0, Math.round(playback.fromTicks));
      const at = Math.max(live.context.currentTime + SESSION_START_LEAD, this.lastStopAt + 0.005);
      // The offset sets the position AT `at`. (Assigning `transport.ticks` first would set it
      // at now() + lookAhead — after `at` — and the music would start that much late.)
      transport.start(at, `${from}i`);
      // Clips already sounding at the start position join from the matching point.
      const spt = secondsPerTick(clampBpm(project.settings.bpm));
      for (const clip of live.timeline.clips) {
        const into = (from - clip.tick) * spt;
        if (into > 0.001 && into < clip.playSec) live.graph.playClip(clip, at, into);
      }
      this.state.update({ status: 'playing', mode: this.snapshot.mode, contextState: live.context.state, totalSteps: live.timeline.totalSteps });
      return at;
    } catch (error) {
      if (error instanceof SessionStartCancelled) throw error;
      this.session = null;
      this.releaseAudio();
      await (this.live?.context.rawContext as AudioContext | undefined)?.suspend?.().catch(() => undefined);
      const message = error instanceof Error ? error.message : 'Audio could not start.';
      this.state.update({ status: 'error', error: message, contextState: this.live?.context.state ?? this.snapshot.contextState });
      throw error;
    } finally {
      debugCounters.audioState = this.snapshot.status;
    }
  }

  /** Stops the transport at the current context time (not at now() + lookAhead). */
  private stopTransportNow(live: Live): void {
    const t = live.context.currentTime;
    live.tone.getTransport().stop(t);
    this.lastStopAt = t;
  }

  /**
   * The AudioContext time being heard right now (seconds), or null when no context is
   * running. Uses `getOutputTimestamp()` (output-latency aware, interpolated with
   * performance.now()) and falls back to `currentTime − outputLatency`.
   */
  audibleTime(): number | null {
    const live = this.live;
    if (!live || live.context.state !== 'running') return null;
    const raw = live.context.rawContext as AudioContext;
    const ts = typeof raw.getOutputTimestamp === 'function' ? raw.getOutputTimestamp() : null;
    if (ts && ts.contextTime !== undefined && ts.performanceTime !== undefined && ts.contextTime > 0) {
      return ts.contextTime + (performance.now() - ts.performanceTime) / 1000;
    }
    return raw.currentTime - (raw.outputLatency || raw.baseLatency || 0);
  }

  /**
   * Phase 4A diagnostics: the transport's own position (ticks) at an AudioContext time —
   * read from Tone's tick clock, independently of the session clock (drift measurement).
   */
  transportTicksAt(contextTime: number): number | null {
    const live = this.live;
    if (!live || this.snapshot.status !== 'playing') return null;
    return live.tone.getTransport().getTicksAtTime(contextTime);
  }

  async toggle(): Promise<void> {
    if (this.snapshot.status === 'playing') await this.stop();
    else await this.start();
  }

  async dispose(): Promise<void> {
    if (this.disposed) return;
    this.disposed = true;
    const live = this.live ?? (this.building ? await this.building.catch(() => null) : null);
    this.live = null;
    this.releaseAudio();
    if (live) {
      const transport = live.tone.getTransport();
      transport.stop();
      transport.cancel();
      live.part?.dispose();
      live.graph.releaseAll();
      live.graph.dispose();
      try {
        await live.context.close();
      } catch {
        // Context may already be closed.
      }
      debugCounters.audioEnginesDisposed++;
    }
    this.state.update({ status: 'idle', step: -1, sectionId: null, scheduledParts: 0, contextState: live ? 'closed' : 'none' });
    debugCounters.audioState = 'disposed';
  }

  private reschedule(live: Live): void {
    const project = this.project;
    if (!project) return;
    const transport = live.tone.getTransport();
    live.part?.dispose();
    live.part = null;
    if (live.playheadId !== null) transport.clear(live.playheadId);
    if (live.endId !== null) transport.clear(live.endId);
    live.playheadId = live.endId = null;

    const timeline = this.timelineFor(project);
    live.timeline = timeline;
    live.part = scheduleTimeline(live.tone, live.graph, timeline.events, timeline.clips);
    const loopEnd = Math.max(TICKS_PER_STEP, timeline.totalTicks);
    const session = this.session;
    if (session) {
      // The session controller owns the end: the transport loops the session's loop range
      // (or runs on silently past the music), never stops by itself.
      transport.loop = session.loop !== null;
      transport.loopStart = `${session.loop?.startTicks ?? 0}i`;
      transport.loopEnd = `${session.loop?.endTicks ?? loopEnd}i`;
    }
    const loops = session ? true : project.transport.mode === 'pattern' || project.transport.loopSong;
    if (!session) {
      transport.loop = loops;
      transport.loopStart = 0;
      transport.loopEnd = `${loopEnd}i`;
    }
    if (!loops) {
      // Song mode without looping: stop cleanly when the arrangement ends.
      // A plain timer, not Tone's Draw queue: Draw drops callbacks more than 0.25 s late
      // (a hidden tab, a busy main thread), which left the transport running past the end.
      const run = this.sessionRequest;
      live.endId = transport.scheduleOnce((time) => {
        const delayMs = Math.max(0, (time - live.context.currentTime) * 1000);
        setTimeout(() => {
          if (run === this.sessionRequest && this.snapshot.status === 'playing') void this.stop();
        }, delayMs);
      }, `${loopEnd}i`);
    }
    live.playheadId = transport.scheduleRepeat((time) => {
      const ticks = transport.getTicksAtTime(time);
      const step = Math.floor(ticks / TICKS_PER_STEP) % Math.max(1, timeline.totalSteps);
      live.tone.getDraw().schedule(() => {
        if (this.snapshot.status !== 'playing') return;
        this.state.update({ step, sectionId: sectionAtStep(timeline, step)?.id ?? null });
      }, time);
    }, '16n');
    live.scheduled = { music: musicOf(project), session: sessionKey(this.session) };
    this.state.update({ totalSteps: timeline.totalSteps, scheduledParts: 1 });
  }

  private ensureLive(): Promise<Live> {
    if (this.live) return Promise.resolve(this.live);
    this.building ??= this.build().then(
      (live) => {
        this.live = live;
        return live;
      },
      (error) => {
        this.building = null;
        throw error;
      },
    );
    return this.building;
  }

  private async build(): Promise<Live> {
    const tone = await this.loadTone();
    const project = this.project!;
    const context = new tone.Context({ latencyHint: 'interactive' });
    // Importing Tone creates a default context (a second AudioContext with its own clock
    // worker); dispose it so the engine's context is the page's only one.
    tone.setContext(context, true);
    debugCounters.audioEnginesStarted++;
    const transport = tone.getTransport();
    transport.PPQ = PPQ;
    transport.swing = 0; // swing is applied by the compiler so export matches playback
    transport.bpm.value = clampBpm(project.settings.bpm);
    // The instrument graph is plain Web Audio on Tone's context; Tone drives the transport.
    const raw = context.rawContext as unknown as BaseAudioContext;
    const graph = buildGraph(raw, raw.destination, project.tracks, project.settings.masterVolumeDb, project.settings.bpm, {
      meter: true,
      audio: this.audio,
      bus: project.audioBus,
    });
    this.state.update({ bpm: clampBpm(project.settings.bpm) });
    return { tone, context, graph, part: null, playheadId: null, endId: null, timeline: this.timelineFor(project), scheduled: null };
  }
}
